import {
  LEARNING_HUB_CATEGORY_PATH,
  LEARNING_HUB_DEFAULT_LANGUAGE,
  LEARNING_HUB_LANGUAGES,
  LEARNING_HUB_IMAGE_PLACEHOLDER_CLASS,
  LEARNING_HUB_MEDIA_PREFIX,
  LEARNING_HUB_MODULE_PATH
} from '../definitions/learning-hub.constants';
import {
  LearningHubArticleMeta,
  LearningHubCategories,
  LearningHubCategory,
  LearningHubFilter,
  LearningHubLanguage,
  LearningHubLanguageOption
} from '../definitions/learning-hub.models';

export const isLearningHubLanguage = (value: unknown): value is LearningHubLanguage =>
  typeof value === 'string' && (LEARNING_HUB_LANGUAGES as string[]).includes(value);

/**
 * Picks the language to show: explicit choice (route/query param) first, then the app language
 * (TranslateService current/default lang, e.g. "es" or "pt-BR"), then the Learning Hub default.
 */
export const resolveLanguage = (...candidates: (string | null | undefined)[]): LearningHubLanguage => {
  for (const candidate of candidates) {
    const short = (candidate || '').toLowerCase().split(/[-_]/)[0];
    if (isLearningHubLanguage(short)) {
      return short;
    }
  }
  return LEARNING_HUB_DEFAULT_LANGUAGE;
};

/** Lowercase, accent-insensitive, single-spaced text used for searching. */
export const normalizeText = (value: string): string =>
  (value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/** Maps a category key or a legacy WordPress nicename (e.g. "create-es") to the normalized key. */
export const resolveCategoryKey = (value: string, categories: LearningHubCategory[]): string | null => {
  if (!value) {
    return null;
  }
  const found = categories.find(
    (category: LearningHubCategory) => category.key === value || category.aliases.includes(value)
  );
  return found ? found.key : null;
};

const searchableText = (article: LearningHubArticleMeta, categoryLabels: Record<string, string>): string =>
  normalizeText(
    [
      article.title,
      article.summary,
      ...article.tags.map(({ name }: { name: string }) => name),
      ...article.categories.map((key: string) => categoryLabels[key] || key)
    ].join(' ')
  );

/**
 * Filters the index of one language. Every word of the query must appear (AND search) in the title,
 * summary, tags or category labels. Category and tag filters are exact matches on normalized keys.
 */
export const filterArticles = (
  articles: LearningHubArticleMeta[],
  filter: LearningHubFilter,
  categoryLabels: Record<string, string> = {}
): LearningHubArticleMeta[] => {
  const words = normalizeText(filter.query || '').split(' ').filter(Boolean);
  return articles.filter((article: LearningHubArticleMeta) => {
    if (filter.category && !article.categories.includes(filter.category)) {
      return false;
    }
    if (filter.tag && !article.tags.some(({ slug }: { slug: string }) => slug === filter.tag)) {
      return false;
    }
    if (!words.length) {
      return true;
    }
    const text = searchableText(article, categoryLabels);
    return words.every((word: string) => text.includes(word));
  });
};

const findCategory = (key: string, categories: LearningHubCategory[]): LearningHubCategory | undefined =>
  categories.find((category: LearningHubCategory) => category.key === key);

/** Top-level sections (the cards of the home), in the order of the WordPress home. */
export const navigationSections = (tree: LearningHubCategories): LearningHubCategory[] =>
  tree.navigation
    .map((key: string) => findCategory(key, tree.categories))
    .filter((category: LearningHubCategory | undefined): category is LearningHubCategory => !!category);

/** Children of a section that have articles in the language, in the order of categories.json. */
export const categoryChildren = (
  category: LearningHubCategory,
  categories: LearningHubCategory[],
  lang: LearningHubLanguage
): LearningHubCategory[] =>
  category.children
    .map((key: string) => findCategory(key, categories))
    .filter((child: LearningHubCategory | undefined) => !!child && (child.articles[lang] || []).length > 0);

/** Router commands of a category page: /learning-hub/category/:parent or /learning-hub/category/:parent/:child. */
export const categoryCommands = (key: string, categories: LearningHubCategory[]): string[] => {
  const category = findCategory(key, categories);
  if (!category) {
    return ['/', LEARNING_HUB_MODULE_PATH];
  }
  const path = category.parent ? [category.parent, category.key] : [category.key];
  return ['/', LEARNING_HUB_MODULE_PATH, LEARNING_HUB_CATEGORY_PATH, ...path];
};

/** Parent first, then the category itself (breadcrumb Learning Hub › Parent › Child). */
export const categoryTrail = (key: string, categories: LearningHubCategory[]): LearningHubCategory[] => {
  const category = findCategory(key, categories);
  if (!category) {
    return [];
  }
  const parent = category.parent ? findCategory(category.parent, categories) : undefined;
  return parent ? [parent, category] : [category];
};

/** The most specific navigable category of an article (a child before its parent), for breadcrumbs. */
export const primaryCategory = (
  article: LearningHubArticleMeta,
  categories: LearningHubCategory[]
): LearningHubCategory | null => {
  const nodes = article.categories
    .map((key: string) => findCategory(key, categories))
    .filter((category: LearningHubCategory | undefined): category is LearningHubCategory => !!category);
  const navigable = nodes.filter((category: LearningHubCategory) => category.navigation);
  return navigable.find((category: LearningHubCategory) => !!category.parent) || navigable[0] || nodes[0] || null;
};

/** Articles of a category (its own and its children's) in a language, in index order (newest first). */
export const categoryArticles = (
  category: LearningHubCategory,
  articles: LearningHubArticleMeta[],
  lang: LearningHubLanguage
): LearningHubArticleMeta[] => {
  const ids = new Set(category.articles[lang] || []);
  return articles.filter((article: LearningHubArticleMeta) => ids.has(article.id));
};

/** "Staff Pick" entries resolved to the articles of a language (entries without that translation are skipped). */
export const staffPickArticles = (
  tree: LearningHubCategories,
  articles: LearningHubArticleMeta[],
  lang: LearningHubLanguage
): LearningHubArticleMeta[] => {
  const byId = new Map(articles.map((article: LearningHubArticleMeta) => [article.id, article]));
  return tree.staffPicks
    .map((pick: Partial<Record<LearningHubLanguage, number>>) => byId.get(pick[lang]))
    .filter((article: LearningHubArticleMeta | undefined): article is LearningHubArticleMeta => !!article);
};

/** True when the segments after /category/ are already the canonical keys (parent, or parent + child). */
export const isCanonicalCategory = (segments: string[], categories: LearningHubCategory[]): boolean => {
  const [first, second] = segments;
  const parent = findCategory(first, categories);
  if (!parent || parent.parent) {
    return false;
  }
  return second === undefined || findCategory(second, categories)?.parent === parent.key;
};

/**
 * Resolves an old WordPress category URL (the segments after /category/, nicenames or keys; the most specific
 * one wins) to a category key and the language the old URL belonged to. The language comes from the URL prefix
 * (/en/, /pt/) when there is one; otherwise from the nicename (e.g. "analyze-es" only exists in Spanish), with
 * Spanish first because it was the language without prefix. Returns null when nothing matches.
 */
export const resolveLegacyCategory = (
  segments: string[],
  categories: LearningHubCategory[],
  prefixLang: LearningHubLanguage | null = null
): { key: string; lang: LearningHubLanguage | null } | null => {
  for (const segment of [...segments].reverse()) {
    const byKey = findCategory(segment, categories);
    if (byKey) {
      return { key: byKey.key, lang: prefixLang };
    }
    const byAlias = categories.find((category: LearningHubCategory) => category.aliases.includes(segment));
    if (byAlias) {
      if (prefixLang) {
        return { key: byAlias.key, lang: prefixLang };
      }
      const langs = LEARNING_HUB_LANGUAGES.filter((lang: LearningHubLanguage) =>
        (byAlias.legacyPaths[lang] || '').endsWith(`/${segment}/`)
      );
      const lang = langs.includes(LEARNING_HUB_DEFAULT_LANGUAGE) ? LEARNING_HUB_DEFAULT_LANGUAGE : langs[0] || null;
      return { key: byAlias.key, lang };
    }
  }
  return null;
};

/** Language selector options for an article: its own language plus the linked Polylang translations. */
export const languageOptions = (article: LearningHubArticleMeta): LearningHubLanguageOption[] =>
  LEARNING_HUB_LANGUAGES.map((lang: LearningHubLanguage) => {
    if (lang === article.lang) {
      return { lang, available: true, slug: article.slug };
    }
    const translation = article.translations[lang];
    return translation ? { lang, available: true, slug: translation.slug } : { lang, available: false };
  });

const escapeAttribute = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const PLACEHOLDER_ATTRIBUTE = /(src|href)="(lh-media:|lh-route:)([^"]*)"/g;

const joinUrl = (base: string, path: string): string => `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;

const UNRESOLVED_IMAGE = /<img\b([^>]*?)\ssrc="lh-media:[^"]*"([^>]*)>/g;
const UNRESOLVED_MEDIA_HREF = /\shref="lh-media:[^"]*"/g;
const ALT_ATTRIBUTE = /\salt="([^"]*)"/;

/**
 * Replaces the placeholders written by the converter:
 *  - `lh-media:<path>` becomes `<mediaBaseUrl>/<path>` (images not mapped to a public URL by the converter).
 *    With an empty mediaBaseUrl those images become a placeholder with their alt text (no request is made)
 *    and links to them lose their href.
 *  - `lh-route:<lang>/<slug>` and `lh-route:?query` become links to the internal routes.
 * The result still goes through Angular's sanitizer when bound with [innerHTML].
 */
export const resolveArticleHtml = (
  html: string,
  options: { mediaBaseUrl: string; routeBase: string }
): string => {
  let resolved = html;
  if (!options.mediaBaseUrl) {
    resolved = resolved
      .replace(UNRESOLVED_IMAGE, (_match: string, before: string, after: string) => {
        const alt = (ALT_ATTRIBUTE.exec(`${before} ${after}`) || [])[1];
        return `<span class="${LEARNING_HUB_IMAGE_PLACEHOLDER_CLASS}">${alt || ''}</span>`;
      })
      .replace(UNRESOLVED_MEDIA_HREF, '');
  }
  return resolved.replace(PLACEHOLDER_ATTRIBUTE, (_match: string, attr: string, prefix: string, rest: string) => {
    const raw = rest.replace(/&amp;/g, '&');
    const url =
      prefix === LEARNING_HUB_MEDIA_PREFIX
        ? joinUrl(options.mediaBaseUrl, raw)
        : raw.startsWith('?')
          ? `${options.routeBase}${raw}`
          : joinUrl(options.routeBase, raw);
    return `${attr}="${escapeAttribute(url)}"`;
  });
};

/** Returns the router URL behind an internal link href (handles hash routing "#/..."). */
export const routerUrlFromHref = (href: string): string => {
  const value = href.startsWith('#') ? href.substring(1) : href;
  return value.startsWith('/') ? value : `/${value}`;
};
